import React from 'react';
import { theme } from '@noths/polaris-client-ribbons-base';
import { pxToRem } from '@noths/polaris-client-styles';

import * as styles from './DepartmentsNavItem.styles';
import { useTracking } from 'client-site-chrome-ui/components/molecules/DepartmentsNav/hooks/useTracking/useTracking';
import { MegaNav } from 'client-site-chrome-ui/components/molecules/MegaNav/MegaNav';
import type { Level1Item } from 'client-site-chrome-ui/types/departmentsNav';

export interface DepartmentsNavItemProps {
  color?: string;
  department: Level1Item;
  index: number;
  openIndex: number;
  setNavClosed: () => void;
  setNavOpen: (index: number) => void;
  setRef: (ref: HTMLAnchorElement) => void;
}

export const DepartmentsNavItem = ({
  color = 'inherit',
  department,
  index,
  openIndex,
  setNavClosed,
  setNavOpen,
  setRef,
}: DepartmentsNavItemProps) => {
  const isThisDeptNavOpen = openIndex === index;
  const { items, title, url } = department;
  const hasChildren = items.length > 0;

  const tracking = useTracking();

  const handleBlur = (e: React.FocusEvent) => {
    const focusIsStillWithinThisDept = e.currentTarget.contains(e.relatedTarget as Node);
    if (!focusIsStillWithinThisDept) {
      setNavClosed();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setNavClosed();
    }
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (isThisDeptNavOpen) {
        setNavClosed();
      } else {
        setNavOpen(index);
      }
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (isThisDeptNavOpen) {
      setNavClosed();
    } else {
      setNavOpen(index);
      tracking.onMegaNavOpen(department);
    }
  };

  return (
    <li
      css={styles.item}
      key={index}
      onBlur={handleBlur}
      style={
        {
          '--department-link-color': color || 'inherit',
          '--horizontal-padding': pxToRem(theme.spacing.lg),
        } as styles.LinkCSSProperties
      }
    >
      <a
        css={styles.link}
        data-active={isThisDeptNavOpen}
        href={url}
        onClick={handleClick}
        onKeyDown={handleKeyPress}
        ref={setRef}
      >
        {title}
      </a>
      {hasChildren && isThisDeptNavOpen && (
        <MegaNav
          departmentTitle={department.title}
          departmentUrl={department.url}
          level2Items={items}
        />
      )}
    </li>
  );
};
